import React, { useState } from 'react';
import { Flex, Spinner } from '@leagueplatform/genesis-commons';
import {
  TeamChallengeRank,
  UserChallengeRank,
} from '@leagueplatform/health-journey-api';
import { useUniqueId } from '@leagueplatform/web-common';
import { ErrorState } from '@leagueplatform/web-common-components';
import { useIntl } from '@leagueplatform/locales';
import {
  HeadingText,
  ParagraphText,
  Popover,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { LeaderboardList } from '#components/leaderboard/leaderboard-list.component.tsx';
import { EmptyActivityState } from '#components/empty-activity-state/empty-activity-state.component.tsx';
import { useGetUserProfile } from '#hooks/use-get-user-profile.hook.ts';
import { LEADERBOARD_SPINNER_FIXED_HEIGHT } from '#constants/challenges.constants.tsx';
import { GlobalLeaderboardToggle } from './components/global-leaderboard-toggle.component';
import { LeaderboardPodium } from './components/leaderboard-podium.component';
import {
  LeaderboardView,
  useGetLeaderboardData,
} from './hooks/use-get-leaderboard-data.hook';

export interface LeaderboardProps {
  teamsEnabled: boolean;
}

export const Leaderboard = ({
  teamsEnabled,
}: LeaderboardProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const [leaderboardView, setLeaderboardView] =
    useState<LeaderboardView>('individual');
  const allTimeRankingHeadingId = useUniqueId({ prefix: 'all-time-ranking' });

  const {
    isLoading,
    error,
    data: leaderboardData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetLeaderboardData(leaderboardView, teamsEnabled);

  const { isLoading: isUserLoading } = useGetUserProfile();
  const flattenedLeaderboardData =
    leaderboardData?.pages?.flatMap<UserChallengeRank | TeamChallengeRank>(
      (p) => p.data,
    ) ?? [];

  if (error) {
    return (
      <GlobalLeaderboardToggle
        teamsEnabled={teamsEnabled}
        onLeaderboardViewChange={setLeaderboardView}
        isLoadingOrError={isLoading || !!error}
      >
        <Flex justifyContent="center" marginY="five">
          <ErrorState error={error} />
        </Flex>
      </GlobalLeaderboardToggle>
    );
  }

  if (isLoading || isUserLoading)
    return (
      <GlobalLeaderboardToggle
        teamsEnabled={teamsEnabled}
        onLeaderboardViewChange={setLeaderboardView}
        isLoadingOrError={isLoading || !!error}
      >
        <Flex
          height={LEADERBOARD_SPINNER_FIXED_HEIGHT}
          justifyContent="center"
          color="decorative.brand.primary.default"
        >
          <Spinner isLoading />
        </Flex>
      </GlobalLeaderboardToggle>
    );

  if (flattenedLeaderboardData.length === 0) {
    return (
      <EmptyActivityState
        activityDescription={formatMessage({
          id: 'NO_LEADERBOARD_ACTIVITY_DESCRIPTION',
        })}
      />
    );
  }

  const { tooltipText, headerText } = leaderboardData?.pages[0].meta ?? {};

  return (
    <>
      <GlobalLeaderboardToggle
        teamsEnabled={teamsEnabled}
        onLeaderboardViewChange={setLeaderboardView}
        isLoadingOrError={isLoading || !!error}
      >
        <LeaderboardPodium
          leaderboardData={flattenedLeaderboardData}
          headerText={headerText ?? ''}
        />
      </GlobalLeaderboardToggle>
      <StackLayout
        orientation="horizontal"
        horizontalAlignment="spaceBetween"
        verticalAlignment="center"
        css={{ padding: '$one' }}
      >
        <HeadingText size="lg" level="2" id={allTimeRankingHeadingId}>
          {formatMessage({ id: 'LEADERBOARD_ALL_TIME_RANKING' })}
        </HeadingText>
        <Popover.Root>
          <Popover.Trigger
            label={formatMessage({ id: 'TOOLTIP_ACTION_DESCRIPTION' })}
            priority="tertiary"
            size="medium"
            quiet
            hideLabel
            icon="interfaceQuestionCircle"
            css={{
              '&:hover': {
                backgroundColor: '$onSurfaceBackgroundPrimary',
                color: '$onSurfaceIconPrimary',
              },
            }}
          />
          <Popover.Content align="end" side="top" css={{ width: '250px' }}>
            <ParagraphText size="sm">{tooltipText}</ParagraphText>
          </Popover.Content>
        </Popover.Root>
      </StackLayout>
      <LeaderboardList
        aria-labelledby={allTimeRankingHeadingId}
        leaderboardData={flattenedLeaderboardData}
        displayViewMore={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        leaderboardView={leaderboardView}
      />
    </>
  );
};
